import { useState } from 'react';

const STRATEGIES = [
  { key: 'MOMENTUM', name: 'Momentum', desc: 'Strongest directional price moves across 24h / 7d / 30d' },
  { key: 'VOLUME_BREAKOUT', name: 'Volume Breakout', desc: 'Volume spikes above 3× rolling average with price confirmation' },
  { key: 'MEAN_REVERSION', name: 'Mean Reversion', desc: 'Oversold bounces near major support levels' },
  { key: 'NEWS_DRIVEN', name: 'News-Driven', desc: 'Tokens with high-impact headlines (≥ 0.5) in last 24h' },
];

/**
 * Onboarding Step 2: Pick Research Strategy.
 * Per wireframe OnboardingA — radio card list with strategy descriptions.
 */
export function OnboardingStep2({ config, agentId, jwtToken, onComplete, onBack }) {
  const [selected, setSelected] = useState('MOMENTUM');
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const handleSubmit = async () => {
    setSubmitting(true);
    setError(null);
    try {
      const res = await fetch(`${config.apiBaseUrl}/agents/${agentId}/research/config`, {
        method: 'POST',
        headers: { Authorization: `Bearer ${jwtToken}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({
          bucket: selected,
          is_active: true,
          methodology_prompt: `${selected} research strategy`,
        }),
      });
      if (!res.ok) throw new Error(`Create research config failed (${res.status})`);
      onComplete({ bucket: selected });
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="flex flex-col gap-4 mt-2">
      <div>
        <div className="text-sm font-semibold text-white mb-1">Pick a research strategy</div>
        <div className="text-xs text-slate-400">Your agent will focus its market scans here. You can change this anytime.</div>
      </div>

      <div className="flex flex-col gap-3">
        {STRATEGIES.map(s => (
          <button
            key={s.key}
            onClick={() => setSelected(s.key)}
            className={`flex items-center gap-3 p-3 rounded-lg border text-left transition-colors ${
              selected === s.key
                ? 'border-cyan-500 bg-cyan-950/30'
                : 'border-slate-700 bg-slate-800/50 hover:border-slate-600'
            }`}
          >
            <div className={`w-5 h-5 rounded-full border-2 flex items-center justify-center flex-shrink-0 ${
              selected === s.key ? 'border-cyan-400 bg-cyan-600' : 'border-slate-600'
            }`}>
              {selected === s.key && <span className="text-white text-xs">✓</span>}
            </div>
            <div className="min-w-0">
              <div className="text-sm font-semibold text-white">{s.name}</div>
              <div className="text-xs text-slate-400">{s.desc}</div>
            </div>
          </button>
        ))}
      </div>

      {error && <p className="text-red-400 text-xs">{error}</p>}

      <hr className="border-slate-700 border-dashed" />

      <div className="flex items-center justify-between">
        <button onClick={onBack} className="text-xs text-slate-400 hover:text-slate-200">← back</button>
        <div className="flex items-center gap-3">
          <span className="text-xs text-slate-500">step 2/4 · ~30 sec left</span>
          <button
            onClick={handleSubmit}
            disabled={submitting}
            className={`px-4 py-2 rounded text-sm font-medium text-white ${
              submitting ? 'bg-cyan-900 opacity-50' : 'bg-cyan-600 hover:bg-cyan-500'
            }`}
          >
            {submitting ? 'Saving…' : 'continue →'}
          </button>
        </div>
      </div>
    </div>
  );
}
